"use client";

import type { ReactNode } from "react";
import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { COOKIEBOT_ENABLED } from "@/lib/cookiebot";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://eu.i.posthog.com";

if (typeof window !== "undefined" && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    disable_session_recording: true,
    person_profiles: "identified_only",
    opt_out_capturing_by_default: COOKIEBOT_ENABLED
  });
}

function applyConsent() {
  const consent = window.Cookiebot?.consent;
  if (consent?.statistics) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
  }
}

function ConsentSync() {
  useEffect(() => {
    if (!COOKIEBOT_ENABLED) return;

    if (window.Cookiebot?.consent) applyConsent();

    window.addEventListener("CookiebotOnAccept", applyConsent);
    window.addEventListener("CookiebotOnDecline", applyConsent);
    window.addEventListener("CookiebotOnConsentReady", applyConsent);

    return () => {
      window.removeEventListener("CookiebotOnAccept", applyConsent);
      window.removeEventListener("CookiebotOnDecline", applyConsent);
      window.removeEventListener("CookiebotOnConsentReady", applyConsent);
    };
  }, []);

  return null;
}

function PageviewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pathname) return;
    let url = window.origin + pathname;
    const search = searchParams?.toString();
    if (search) url += `?${search}`;
    posthog.capture("$pageview", { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  if (!POSTHOG_KEY) return <>{children}</>;

  return (
    <PHProvider client={posthog}>
      <ConsentSync />
      <Suspense fallback={null}>
        <PageviewTracker />
      </Suspense>
      {children}
    </PHProvider>
  );
}
